import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { OverviewTiles } from '../components/admin/disputes/OverviewTiles';
import { SlaWatchTable } from '../components/admin/disputes/SlaWatchTable';
import { QueueFilters } from '../components/admin/disputes/QueueFilters';
import { QueueTable } from '../components/admin/disputes/QueueTable';
import { DisputesLeaderboard } from '../components/admin/disputes/DisputesLeaderboard';
import { DisputeDetailDrawer } from '../components/admin/disputes/DisputeDetailDrawer';

export default function AdminDisputesPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [range, setRange] = useState(searchParams.get('range') || '30d');
  const [selectedDisputeId, setSelectedDisputeId] = useState(null);
  
  // Get filters from URL
  const status = searchParams.get('status') || 'open,needs_moderation';
  const reason = searchParams.get('reason') || '';
  const tier = searchParams.get('tier') || '';
  const categoryId = searchParams.get('categoryId') || '';
  const ageHours = searchParams.get('age_hours') || '';
  const search = searchParams.get('search') || '';
  const cursor = searchParams.get('cursor') || '';
  
  const handleRangeChange = (newRange) => {
    setRange(newRange);
    const params = new URLSearchParams(searchParams);
    params.set('range', newRange);
    setSearchParams(params);
  };
  
  const handleFilterChange = (filters) => {
    const params = new URLSearchParams();
    params.set('range', range);
    
    if (filters.status) params.set('status', filters.status);
    if (filters.reason) params.set('reason', filters.reason);
    if (filters.tier) params.set('tier', filters.tier);
    if (filters.categoryId) params.set('categoryId', filters.categoryId);
    if (filters.ageHours) params.set('age_hours', filters.ageHours);
    if (filters.search) params.set('search', filters.search);
    if (filters.cursor) params.set('cursor', filters.cursor);
    
    setSearchParams(params);
  };
  
  const handleOpenDispute = (disputeId) => {
    setSelectedDisputeId(disputeId);
  };
  
  const handleCloseDrawer = () => {
    setSelectedDisputeId(null);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Disputes</h1>
              <p className="mt-1 text-sm text-gray-600">
                Monitor and moderate buyer-seller disputes
              </p>
            </div>
            
            {/* Range Picker */}
            <Select value={range} onValueChange={handleRangeChange}>
              <SelectTrigger className="w-32">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="7d">7 days</SelectItem>
                <SelectItem value="30d">30 days</SelectItem>
                <SelectItem value="90d">90 days</SelectItem>
                <SelectItem value="all">All time</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Main Column */}
          <div className="lg:col-span-3 space-y-6">
            {/* Overview KPIs */}
            <OverviewTiles range={range} />
            
            {/* SLA Watch */}
            <SlaWatchTable onOpenDispute={handleOpenDispute} />
            
            {/* Queue */}
            <Card>
              <div className="p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">Disputes Queue</h2>
                
                {/* Filters */}
                <QueueFilters
                  status={status}
                  reason={reason}
                  tier={tier}
                  categoryId={categoryId}
                  ageHours={ageHours}
                  search={search}
                  onFilterChange={handleFilterChange}
                />
                
                {/* Table */}
                <QueueTable
                  status={status}
                  reason={reason}
                  tier={tier}
                  categoryId={categoryId}
                  ageHours={ageHours}
                  search={search}
                  cursor={cursor}
                  onOpenDispute={handleOpenDispute}
                  onPageChange={(newCursor) => handleFilterChange({ 
                    status, reason, tier, categoryId, ageHours, search, cursor: newCursor 
                  })}
                />
              </div>
            </Card>
          </div>
          
          {/* Sidebar */}
          <div className="lg:col-span-1">
            <DisputesLeaderboard range="90d" />
          </div>
        </div>
      </div>
      
      {/* Detail Drawer */}
      <DisputeDetailDrawer
        disputeId={selectedDisputeId}
        isOpen={!!selectedDisputeId}
        onClose={handleCloseDrawer}
      />
    </div>
  );
}
